<template>
  <div>
  <PageHeader title="Studies" subtitle="Every study, its inputs and its latest result" :breadcrumbs="BASE_CRUMBS" />
  <div class="p-6 pb-32 text-[11px] space-y-4">
    <div class="flex items-center gap-3">
      <input v-model="search" type="search" placeholder="Search studies" aria-label="Search studies"
             class="w-72 px-3 py-2 border border-gray-300 rounded bg-white">
      <label class="inline-flex items-center gap-2 text-gray-700">
        <input v-model="showAgent" type="checkbox" class="rounded border-gray-300"> Show agent working studies
      </label>
      <span class="text-gray-500">{{ shown.length }} of {{ studies.length }}</span>
      <router-link to="/insight/impact-analytics/studies/new" class="btn-primary-sm ml-auto">New study</router-link>
    </div>

    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <LoadingIndicator v-if="loading" message="Loading studies..." />
      <div v-else-if="error" class="px-4 py-6 text-red-dark">{{ error }}</div>
      <table v-else class="w-full table-fixed divide-y divide-gray-200">
        <colgroup><col style="width:30%"><col style="width:12%"><col style="width:26%"><col style="width:18%"><col style="width:6%"><col style="width:8%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Study</th><th :class="TH">Source</th><th :class="TH">Reads</th>
          <th :class="TH">Last run</th><th :class="[TH, 'text-right']">Runs</th><th :class="TH">Owner</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="!shown.length"><td colspan="6" class="px-3 py-8 text-center text-gray-500">No studies match.</td></tr>
          <tr v-for="s in shown" :key="s.id" class="cursor-pointer hover:bg-gray-50" @click="open(s)">
            <td :class="TD">
              <router-link :to="studyPath(s.id)" class="font-medium text-neutral-800" @click.stop>{{ s.name }}</router-link>
              <div v-if="s.description" class="text-gray-500 truncate" :title="s.description">{{ s.description }}</div>
            </td>
            <td :class="TD">{{ SOURCES[s.origin] }}<div v-if="s.template_key" class="text-gray-500">{{ s.template_key }}</div></td>
            <td :class="TD">
              <div v-for="i in s.inputs" :key="i.source_type + i.slug" class="sensitive truncate">{{ i.slug }}</div>
            </td>
            <td :class="TD">
              <template v-if="s.last_run">
                <HermesBadge :value="s.last_run.status" />
                <span class="ml-2 text-gray-500" :title="absolute(s.last_run.run_at)">{{ relative(s.last_run.run_at) }}</span>
              </template>
              <span v-else class="text-gray-500">Never run</span>
            </td>
            <td :class="[TD, 'text-right tabular-nums']">{{ s.run_count }}</td>
            <td :class="TD"><span class="truncate block">{{ s.created_by_name || '—' }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesBadge from '@/components/Admin/Hermes/HermesBadge.vue';
import { TH, TD, absolute, relative } from '@/components/Admin/Hermes/hermes';
import { BASE_CRUMBS } from './impactNav';

const SOURCES = { builtin: 'Built-in', saved: 'Saved', agent: 'Agent working' };

export default {
  name: 'StudyIndex',
  components: { PageHeader, LoadingIndicator, HermesBadge },
  data() {
    return { studies: [], loading: true, error: '', search: '', showAgent: false, TH, TD, SOURCES, BASE_CRUMBS };
  },
  computed: {
    shown() {
      const q = this.search.trim().toLowerCase();
      return this.studies
        .filter((s) => this.showAgent || s.origin !== 'agent')
        .filter((s) => !q || `${s.name} ${s.description || ''} ${s.inputs.map((i) => i.slug).join(' ')}`.toLowerCase().includes(q))
        .sort((a, b) => String((b.last_run || {}).run_at || '').localeCompare(String((a.last_run || {}).run_at || '')));
    },
  },
  async mounted() {
    try {
      const { data } = await axios.get('api/impact_analytics/studies/');
      this.studies = data;
    } catch (err) {
      this.error = err?.response?.data?.detail || 'Studies could not be loaded.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    absolute,
    relative,
    studyPath(id) {
      return `/insight/impact-analytics/studies/${id}`;
    },
    open(s) {
      this.$router.push(this.studyPath(s.id));
    },
  },
};
</script>
